/* ==========================================================================
   Health Holding programme — context + mandatory embroidery.

   WHY A URL PARAMETER AND NOT SESSION STATE
   The same 100 garments sell as ordinary retail. Embroidery is required only
   for someone shopping the Health Holding programme, so the rule has to travel
   with the journey, not with the product. A `?hh=1` parameter does that and
   self-clears: follow any ordinary link and you are back in normal retail. A
   sessionStorage flag would be stickier, and would strand a shopper who wanders
   out of the programme into an unrelated product with no way to clear it.

   WHAT ENFORCEMENT MEANS HERE
   Add to cart is held until the embroidery panel reports a confirmed choice.
   That signal is the hidden summary input: figs-embroidery keeps it `disabled`
   until the customer confirms, and a disabled input is never submitted. This is
   a UX guard, not a security control — someone determined can still post the
   form. That is acceptable: the worst case is an ordinary un-embroidered order,
   not a mispriced one, because the add-on is a real variant either way.

   WHY THE LOGO IS FETCHED HERE INSTEAD OF INLINED IN LIQUID
   The section used to inline it with `inline_asset_content`. That filter has a
   hard size cap — the storefront answers a 52 KB file with a comment, measured
   verbatim on the draft theme:
     <!-- Asset size is too large ... Size limit: 15360 bytes. -->
   So the hero rendered an empty box. Squeezing the SVG is not a route: it is 58
   potrace paths already at one-decimal precision, and a maximal whitespace and
   precision squeeze still lands near 51 KB — three times the cap.

   It also cannot become an <img> or <object>. The five points of the mark are
   five clipped copies of one outline, each filled `var(--hh-pN)`, and custom
   properties do not cross into an external image — it would render in one flat
   colour and the "five points are the five approved colours" idea, which is the
   whole page, would be gone. Only real inline DOM inherits those properties.
   Hence: fetch the asset and inject it. See injectMark below.
   ========================================================================== */
(function () {
  'use strict';

  var HH_PARAM = 'hh';
  var LOGO_FILE = 'drs-hh-logo.svg';

  /* Our own <script src>, which Liquid wrote with `asset_url`. Everything that
     varies between stores and theme versions — CDN host, shop, the /t/<id>/
     segment — is already in it, so the sibling SVG's URL can be derived from it
     and no CDN path is hardcoded anywhere. Captured at parse time: for a
     deferred script `document.currentScript` is valid during top-level
     execution, but it is null by the time a fetch callback runs. */
  var SELF_SRC = (document.currentScript && document.currentScript.src) || '';

  /* ---- On the programme collection: carry the context onto product links -- */
  function tagProductLinks() {
    if (!document.querySelector('.drs-hh')) return;
    // Scope to the grid. Tagging every /products/ link on the page would drag
    // the nav, footer and cross-sell links into forced-embroidery mode too —
    // which is exactly the retail flow this parameter exists to protect.
    var grid = document.querySelector('.product-grid');
    if (!grid) return;
    grid.querySelectorAll('a[href*="/products/"]').forEach(function (a) {
      var href = a.getAttribute('href');
      if (!href || href.indexOf('/products/') === -1) return;
      if (href.indexOf(HH_PARAM + '=1') !== -1) return;
      a.setAttribute('href', href + (href.indexOf('?') === -1 ? '?' : '&') + HH_PARAM + '=1');
    });
  }

  /* ---- On a product page reached from the programme ---------------------- */
  function enforceOnProduct() {
    var params = new URLSearchParams(window.location.search);
    if (params.get(HH_PARAM) !== '1') return;

    var form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    // No embroidery inputs means this product carries no panel at all. Do
    // nothing rather than block a sale on a product that cannot comply.
    var props = form.querySelectorAll('[data-emb-prop]');
    if (!props.length) return;

    document.documentElement.classList.add('drs-hh-ctx');

    var submit = form.querySelector('[name="add"], button[type="submit"], .add-to-cart');
    if (!submit) return;

    var notice = document.createElement('p');
    notice.className = 'drs-hh-req';
    notice.setAttribute('role', 'status');
    notice.textContent = form.getAttribute('data-hh-required-text') ||
      'Embroidery is required for Health Holding orders. Personalised items cannot be returned.';
    submit.parentNode.insertBefore(notice, submit);

    function sync() {
      // A confirmed choice enables at least one property input; nothing is
      // submitted while they are all disabled. Checking ALL of them matters:
      // only the TEXT option writes `summary`, so keying on that alone would
      // block add-to-cart forever for an icon-only or logo-only order.
      var chosen = Array.prototype.some.call(props, function (i) { return !i.disabled; });
      submit.disabled = !chosen;
      submit.classList.toggle('drs-hh-blocked', !chosen);
      notice.hidden = chosen;
    }

    // figs-embroidery toggles `disabled` directly, so watch the attribute
    // rather than guessing at its internal events.
    var mo = new MutationObserver(sync);
    props.forEach(function (i) {
      mo.observe(i, { attributes: true, attributeFilter: ['disabled'] });
    });
    sync();
  }

  /* ---- The mark: fetched, then injected as real inline DOM --------------- */

  // One fetch per page even if the hero were placed twice.
  var logoText = null;

  function logoUrl(mark) {
    // The section is free to state the URL outright; prefer it when it does.
    var stated = mark.getAttribute('data-logo-src');
    if (stated) return stated;

    // Otherwise sit the filename next to our own: same theme, same directory.
    if (!SELF_SRC) {
      var tag = document.querySelector('script[src*="drs-hh.js"]');
      SELF_SRC = tag ? tag.src : '';
    }
    if (!SELF_SRC) return '';

    // Carry `?v=` across rather than dropping it. Theme assets are served
    // `cache-control: max-age=31557600`, so the bare URL is cached for a YEAR —
    // measured here: after pushing a new drs-hh.js, the versioned URL served the
    // new file while the bare one still served the previous build. Reusing the
    // JS's own version token is not a hash of the SVG, but a CLI push stamps a
    // fresh token on every file it uploads, so the two move together in practice
    // and a stale mark cannot outlive a deploy. Shopify ignores the token's
    // value and serves the asset regardless. For an exact token, give the mark a
    // data-logo-src built from the SVG's own asset_url — see logoUrl above.
    var parts = SELF_SRC.split('#')[0].split('?');
    var path = parts[0].replace(/[^/]+$/, LOGO_FILE);
    return parts[1] ? path + '?' + parts[1] : path;
  }

  // Parse to a document rather than assigning innerHTML: that keeps the SVG
  // namespace explicit, and gives one clear place to drop anything executable
  // before it reaches the page.
  function parseSvg(text) {
    var doc = new DOMParser().parseFromString(text, 'image/svg+xml');
    var root = doc.documentElement;
    if (!root || doc.getElementsByTagName('parsererror').length) return null;
    if (String(root.nodeName).toLowerCase() !== 'svg') return null;
    var scripts = root.querySelectorAll('script, foreignObject');
    for (var i = 0; i < scripts.length; i++) scripts[i].remove();
    return document.importNode(root, true);
  }

  function fillMark(mark) {
    var node = logoText && parseSvg(logoText);
    if (!node) { mark.setAttribute('data-hh-mark', 'absent'); return; }
    mark.appendChild(node);
    // Flips the CSS from reserved-space to rendered. Set last, so a throw on
    // the line above leaves the mark in a state the CSS collapses.
    mark.setAttribute('data-hh-mark', 'ready');
  }

  function injectMark() {
    // The attribute is the double-injection guard: a section re-render in the
    // theme editor hands us fresh DOM without it, an already-served mark keeps
    // it, and re-running this is a no-op.
    var marks = document.querySelectorAll('.drs-hh__mark:not([data-hh-mark])');
    if (!marks.length) return;

    Array.prototype.forEach.call(marks, function (mark) {
      mark.setAttribute('data-hh-mark', 'loading');

      if (logoText) { fillMark(mark); return; }

      var url = logoUrl(mark);
      if (!url || !window.fetch) { mark.setAttribute('data-hh-mark', 'absent'); return; }

      // Every failure path ends at the same place: mark `absent`, which the CSS
      // collapses to nothing. A hero with no mark is quiet; a hero with a
      // reserved empty box where the mark should be is a bug on the screen.
      fetch(url, { credentials: 'omit' })
        .then(function (res) {
          if (!res.ok) throw new Error(res.status);
          return res.text();
        })
        .then(function (text) {
          logoText = text;
          fillMark(mark);
        })
        .catch(function () {
          mark.setAttribute('data-hh-mark', 'absent');
        });
    });
  }

  // Decoration must never be able to take down enforcement. The add-to-cart
  // guard runs first and the mark is wrapped, so a DOMParser or fetch surprise
  // costs a logo, not a correctly-gated order.
  function safeInjectMark() {
    try { injectMark(); } catch (e) { /* the CSS collapses whatever state we left */ }
  }

  function init() { tagProductLinks(); enforceOnProduct(); safeInjectMark(); }

  // Theme editor: re-running is safe, and without this the mark is missing for
  // the merchant every time they nudge a setting.
  document.addEventListener('shopify:section:load', safeInjectMark);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
